import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StripeCardForm } from "@/components/StripeCardForm";
import { HardHat, Check, ShieldCheck, CreditCard, LogOut } from "lucide-react";

const FEATURES = [
  "Unlimited live opportunity matches",
  "AI Qualification & Win probability scoring",
  "Score Any Bid (paste a link, PDF, or text)",
  "Growth Roadmap & Bid CRM pipeline",
  "Competitive Intelligence",
];

export default function StartTrial() {
  const { user, logout, refresh } = useAuth();
  const [activating, setActivating] = useState(false);
  const used = user?.trial_status === "used";

  const onConfirm = async (paymentMethodId) => {
    setActivating(true);
    try {
      await api.post("/payments/start-trial", { payment_method_id: paymentMethodId, plan: "growth", interval: "monthly" });
      toast.success(used ? "Subscription active — welcome back!" : "Your 5-day free trial is active!");
      await refresh();
    } catch (e) {
      setActivating(false);
      toast.error(e?.response?.data?.detail || "Could not activate — please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-4" data-testid="start-trial-screen">
      <div className="absolute top-5 right-5">
        <button onClick={logout} data-testid="trial-logout" className="text-emerald-200/70 hover:text-white text-sm inline-flex items-center gap-1.5 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-emerald-900 px-8 pt-8 pb-6 text-white">
          <div className="flex items-center gap-2 mb-5">
            <div className="h-9 w-9 rounded-lg bg-lime-400/20 flex items-center justify-center">
              <HardHat className="h-5 w-5 text-lime-300" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
          </div>
          <span className="inline-block text-xs font-bold uppercase tracking-wider bg-lime-400 text-emerald-950 rounded-full px-3 py-1 mb-3">
            {used ? "Subscribe to continue" : "5-day free trial"}
          </span>
          <h1 className="font-heading text-2xl font-bold leading-tight" data-testid="trial-heading">
            {used
              ? "Reactivate your BidMatchAI access"
              : "Full access to every feature, free for 5 days"}
          </h1>
          <p className="text-emerald-100/80 text-sm mt-2">
            {used
              ? "Your trial has ended. Subscribe to Growth to keep winning contracts."
              : "Start today. We'll only charge you after your trial — cancel anytime before then and pay nothing."}
          </p>
        </div>

        <div className="px-8 py-7">
          <ul className="space-y-3 mb-6">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          {activating ? (
            <div className="flex items-center justify-center gap-3 py-3 text-slate-600" data-testid="trial-activating">
              <div className="h-5 w-5 border-2 border-slate-200 border-t-emerald-700 rounded-full animate-spin" />
              Activating your access…
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 inline-flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" /> Card details
              </p>
              <StripeCardForm
                submitLabel={used ? "Subscribe — $50/mo" : "Start my 5-day free trial"}
                note={used ? "Billed $50/mo. Cancel anytime. Secured by Stripe." : "After 5 days you'll be billed $50/mo unless you cancel. Secured by Stripe."}
                onConfirm={onConfirm}
              />
            </>
          )}

          <div className="mt-4 text-xs text-slate-400 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Cancel anytime from Billing.
          </div>
        </div>
      </div>
    </div>
  );
}

